import type { OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import type { IOrganizationValidated } from '@common/validation/organization';
import {
    ManagerAuthorization,
    OrganizationDataErrorType,
    OrganizationKgoStatus,
    OrganizationType,
} from '@common/enums';
import { fetchJson } from '@frontend/store/fetch';
import { hasOneAuthorization, type ValidationErrors } from '@frontend/utils';

type Organization = Partial<IOrganizationValidated> & { id?: string };

export const errorTypesByOrganizationProperty: Map<keyof IOrganizationValidated, OrganizationDataErrorType[]> = new Map(
    [
        [
            'agbCodes',
            [
                OrganizationDataErrorType.agbStartDateMismatch,
                OrganizationDataErrorType.duplicateAgbCode,
                OrganizationDataErrorType.expiredAgb,
                OrganizationDataErrorType.invalidAgb,
            ],
        ],
    ]
);

export async function updateValidationErrorsForDataErrors(
    currentErrors: ValidationErrors,
    organization: Organization | undefined,
    ...keys: (keyof IOrganizationValidated)[]
): Promise<{
    dataErrors: OrganizationDataError[];
    validationErrors: ValidationErrors;
}> {
    if (shouldValidateDataErrors(organization) === false) {
        return { dataErrors: [], validationErrors: currentErrors };
    }

    const dataErrors = await checkDataErrors(organization);

    for (const key of keys) {
        if (currentErrors.keySeq().some((currentErrorKey) => currentErrorKey?.startsWith(key) === true)) {
            // Joi schema validation already failed on this key, so skip it.
            continue;
        }

        const relevantDataErrorTypes = errorTypesByOrganizationProperty.get(key);

        if (!relevantDataErrorTypes) {
            continue;
        }

        const dataError = dataErrors.find(({ type }) => relevantDataErrorTypes.includes(type));

        if (dataError) {
            currentErrors = currentErrors.set(key, dataError.type);
        }
    }

    return { dataErrors, validationErrors: currentErrors };
}

export async function checkDataErrors(organization: Organization): Promise<OrganizationDataError[]> {
    return (
        await fetchJson({
            body: organization,
            method: 'POST',
            url: '/api/organizations/dataErrors/check',
        })
    ).json;
}

function shouldValidateDataErrors(organization: Organization | undefined): organization is Organization {
    return (
        organization !== undefined &&
        organization.organizationType !== OrganizationType.testOrganization &&
        organization.kgoStatus === OrganizationKgoStatus.verified &&
        hasOneAuthorization(ManagerAuthorization.organizationAddEdit)
    );
}
